import T from 'i18n-react';
import React, { Component } from 'react';
import InputMask from 'react-input-mask';
import { PaymentVendorProcessType } from '../../../constants/PaymentVendorProcessTypeConstants';
import * as CommonStore from '../../../store/Common';

const SLIMCD_ALLOWED_HOSTS = ['slimcd.com'];

function isSlimCDUrlAllowed(url) {
    try {
        const { protocol, hostname } = new URL(url);
        return (
            protocol === 'https:' &&
            SLIMCD_ALLOWED_HOSTS.some(
                host => hostname === host || hostname.endsWith('.' + host)
            )
        );
    } catch {
        return false;
    }
}

/**
 * SlimCD tokenized payment form (credit card only).
 * Sends a zero-amount LOAD transaction directly to SlimCD through slimcd.js and returns the resulting gateid token.
 * Full PAN/CVV never reaches the application server; only the token and non-sensitive metadata (e.g., last4/expiry) are forwarded.
 */
class SlimCDForm extends Component {
    constructor(props) {
        super(props);

        this.cardNumberValueRef = React.createRef();
        this.expirationRef = React.createRef();
        this.cvvRef = React.createRef();

        this.state = {
            sdkLoaded: false,
            username: '',
            sdkUrl: '',
            cardAccountName: '',
            cardholderName: '',
            fields: [
                { name: 'cardAccountName', isRequired: true, forPaymentType: 'CreditCard' },
                { name: 'cardholderName', isRequired: true, forPaymentType: 'CreditCard', minTextWords: 2, onValidating: CommonStore.hasMinWordsAmount },
                { name: 'cardNumberValue', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCreditCardNumber, isDomValue: true },
                { name: 'expiration', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCreditCardExpDate, isDomValue: true },
                { name: 'cvv', isRequired: true, forPaymentType: 'CreditCard', onValidating: CommonStore.isValidCvv, isDomValue: true }
            ]
        };

        this.handleLoad = this.handleLoad.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.onHideRecessiveFields) {
            this.props.onHideRecessiveFields([
                'cardAccountName',
                'bankAccountName',
                'bankNameOnAccount',
                'cardNameOnAccount'
            ]);
        }

        this.initializeConfig();
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.removeSdkScriptListeners) this.removeSdkScriptListeners();
    }

    initializeConfig = () => {
        try {
            const { content } = this.props;
            const configB64 = CommonStore.decompressString(content);
            const hppConfig = JSON.parse(configB64 || '{}');

            if (hppConfig.Username && hppConfig.Url) {
                this.setState(
                    { username: hppConfig.Username, sdkUrl: hppConfig.Url },
                    this.loadSlimCDSDK
                );
            } else {
                if (this.props.setPaymentError) {
                    this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
                }
                this.handleLoad();
            }
        } catch (error) {
            if (this.props.setPaymentError) {
                this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
            }
            this.handleLoad();
        }
    }

    loadSlimCDSDK = () => {
        const { sdkUrl } = this.state;

        if (!isSlimCDUrlAllowed(sdkUrl)) {
            if (this.props.setPaymentError) {
                this.props.setPaymentError(T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.');
            }
            this.handleLoad();
            return;
        }

        const handleScriptSuccess = () => {
            if (this._isMounted) {
                this.setState({ sdkLoaded: true }, this.handleLoad);
            }
        };

        const handleScriptError = () => {
            // Remove the failed script tag so a later mount injects a fresh one
            // instead of listening on a dead element whose events already fired.
            const failedScript = document.getElementById('slimCDSdk');
            if (failedScript) failedScript.remove();

            if (this._isMounted) {
                if (this.props.setPaymentError) {
                    this.props.setPaymentError(T.translate('accountPay.iframeLoadError') || 'Failed to load payment SDK.');
                }
                this.setState({ sdkLoaded: false }, this.handleLoad);
            }
        };

        const existingScript = document.getElementById('slimCDSdk');

        if (existingScript) {
            // If the script tag exists AND the global object is ready, we are good to go immediately
            if (window.SlimCD) {
                this.setState({ sdkLoaded: true }, this.handleLoad);
            } else {
                // The script exists but is still loading. Attach listeners to the existing script element.
                existingScript.addEventListener('load', handleScriptSuccess, { once: true });
                existingScript.addEventListener('error', handleScriptError, { once: true });
                this.removeSdkScriptListeners = () => {
                    existingScript.removeEventListener('load', handleScriptSuccess);
                    existingScript.removeEventListener('error', handleScriptError);
                };
            }
            return;
        }

        const script = document.createElement('script');
        script.id = 'slimCDSdk';
        script.src = sdkUrl;
        script.type = 'text/javascript';
        script.async = true;

        script.onload = handleScriptSuccess;
        script.onerror = handleScriptError;

        document.head.appendChild(script);
    }

    onChange = (e) => {
        const { name, value } = e.target;
        const fields = [...this.state.fields];
        const fieldIdx = fields.findIndex(f => f.name === name);

        if (fieldIdx !== -1) {
            const field = { ...fields[fieldIdx] };

            if (field.isRequired && field.isRequiredError && value) {
                if (field.onValidating) {
                    field.onValidating(field, e, this.props);
                } else {
                    field.isRequiredError = false;
                }
            }

            fields[fieldIdx] = field;
            this.setState({ fields });

            if (field.isDomValue) return;
        }

        this.setState({ [name]: value });
    }

    isFormValid = () => {
        const { paymentVendorProcessTypeID } = this.props;
        const isAutoPayment = paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment;
        const nicknameFields = ['cardAccountName'];
        let valid = true;

        const fields = this.state.fields.map((field) => {
            const nextField = { ...field };

            if (nextField.isDomValue) {
                nextField.value = this[nextField.name + 'Ref'].current ? this[nextField.name + 'Ref'].current.value : '';
            } else {
                nextField.value = this.state[nextField.name];
            }

            nextField.isRequiredError = false;
            const effectiveIsRequired = nicknameFields.includes(nextField.name) ? isAutoPayment : nextField.isRequired;

            if (effectiveIsRequired && !nextField.value) {
                nextField.isRequiredError = true;
                valid = false;
            }

            if (nextField.onValidating && nextField.value) {
                valid = nextField.onValidating(nextField, undefined, this.props) && valid;
            }

            if (nextField.isDomValue) delete nextField.value;
            return nextField;
        });

        this.setState({ fields });

        if (valid) {
            if (this.props.setPaymentError) this.props.setPaymentError('');
        } else {
            if (this.props.setPaymentError) this.props.setPaymentError(T.translate('common.thereAreMissingFields'));
        }
        return valid;
    }

    onPayClick = (e) => {
        e.preventDefault();
        if (!this.isFormValid()) return;

        if (window.SlimCD && window.SlimCD.Transact && typeof window.SlimCD.Transact.ProcessTransaction === 'function') {

            const btnPay = e.currentTarget;

            btnPay.disabled = true;
            btnPay.style.opacity = '0.5';
            btnPay.style.cursor = 'not-allowed';

            window.SlimCD.Transact.ProcessTransaction(this.getTransactionRequest(), (reply) => this.responseHandler(reply, btnPay));
        } else {
            if (this.props.setPaymentError) {
                this.props.setPaymentError('Payment service is currently unavailable. Please try again later.');
            }
        }
    }

    getTransactionRequest = () => {
        const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value.replace(/[\s_-]/g, '') : '';
        const expiration = this.expirationRef.current ? this.expirationRef.current.value : '';
        const cvv = this.cvvRef.current ? this.cvvRef.current.value : '';
        const [expireMonth = '', expireYearShort = ''] = expiration.split('/');
        const expireYear = expireYearShort.trim();
        const nameParts = this.state.cardholderName.trim().split(' ');

        return {
            username: this.state.username,
            transtype: 'LOAD',
            amount: '0.00',
            cardnumber: cardNumber,
            expmonth: expireMonth.trim(),
            expyear: expireYear.length === 2 ? `20${expireYear}` : expireYear,
            cvv2: cvv,
            first_name: nameParts[0] || '',
            last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
        };
    }

    responseHandler = (reply, btnPay) => {
        if (reply && reply.response === 'Success' && reply.datablock && reply.datablock.gateid) {
            this.handleSuccess(reply);
        } else {
            if (btnPay) {
                btnPay.disabled = false;
                btnPay.style.opacity = '';
                btnPay.style.cursor = '';
            }
            const errorText = (reply && reply.description) || 'An error occurred with the payment service.';
            if (this.props.setPaymentError) this.props.setPaymentError(errorText);
        }
    }

    handleSuccess = (reply) => {
        const { onPaymentSuccess, onPaymentMethodSuccess, paymentVendorProcessTypeID } = this.props;
        const holderName = this.state.cardholderName;
        const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value.replace(/[\s_-]/g, '') : '';
        const expirationParts = this.expirationRef.current ? this.expirationRef.current.value.split('/') : [];
        const expireMonth = expirationParts[0] ? expirationParts[0].trim() : '';
        const expireYearRaw = expirationParts[1] ? expirationParts[1].trim() : '';
        const expireYear = expireYearRaw.length === 2 ? '20' + expireYearRaw : expireYearRaw;

        const normalizedResponse = {
            vendor: 'SlimCD',
            token: String(reply.datablock.gateid),
            nameOnAccount: holderName,
            operation: 'SaveCCToken',
            jsonResponse: JSON.stringify(reply),
            transactionResponse: JSON.stringify(reply),
            last4: cardNumber.slice(-4)
        };

        const buildPaymentData = (includeNickname = false) => {
            const baseData = {
                ...normalizedResponse,
                holderName: holderName
            };

            if (includeNickname) {
                baseData.nickname = this.state.cardAccountName;
            }

            baseData.cardNumber = '************' + baseData.last4;
            baseData.cardType = CommonStore.getCardInfo(cardNumber, false).name;
            baseData.expire_month = expireMonth;
            baseData.expire_year = expireYear;
            baseData.cardExpDate = (expireMonth + expireYearRaw.slice(-2)).length === 4 ? expireMonth + expireYearRaw.slice(-2) : '';

            return baseData;
        };

        if (paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment && onPaymentMethodSuccess) {
            onPaymentMethodSuccess(buildPaymentData(true));
        } else if (onPaymentSuccess) {
            onPaymentSuccess(buildPaymentData(false));
        }
    }

    handleLoad() {
        if (this.props.onLoaded) this.props.onLoaded();
    }

    getCreditCardMask = () => {
        const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value : '';
        return CommonStore.getCardInfo(cardNumber, false).mask;
    }

    getFieldByName = (name) => this.state.fields.find(f => f.name === name) || {};

    renderCreditCard() {
        const { cardholderName, cardAccountName } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        const ccNicknameField = this.getFieldByName('cardAccountName');
        const ccNameField = this.getFieldByName('cardholderName');
        const cardNumberField = this.getFieldByName('cardNumberValue');
        const expirationField = this.getFieldByName('expiration');
        const cvvField = this.getFieldByName('cvv');

        return (
            <div className="slimcd-credit-card">
                {paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment &&
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="cardAccountName">{T.translate('paymentMethod.cardNickname')}</label>
                                <input type="text" id="cardAccountName" name="cardAccountName" value={cardAccountName} onChange={this.onChange} className={'form-control ' + (ccNicknameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                                <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                            </div>
                        </div>
                    </div>
                }
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="cardholderName">{T.translate('accountPay.nameOnCard')}</label>
                            <input type="text" id="cardholderName" name="cardholderName" value={cardholderName} onChange={this.onChange} className={'form-control ' + (ccNameField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('accountPay.holderNameRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="cardNumberValue">{T.translate('accountPay.cardNumber')}</label>
                            <InputMask ref={this.cardNumberValueRef} mask={this.getCreditCardMask()} placeholder="#### #### #### ####" id="cardNumberValue" name="cardNumberValue" onChange={this.onChange} className={'form-control ' + (cardNumberField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="expiration">{T.translate('accountPay.expiration')}</label>
                            <InputMask ref={this.expirationRef} mask="99/99" placeholder="MM/YY" id="expiration" name="expiration" onChange={this.onChange} className={'form-control ' + (expirationField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="cvv">{T.translate('accountPay.securityCode')}</label>
                            <input type="password" inputMode="numeric" pattern="[0-9]*" ref={this.cvvRef} id="cvv" name="cvv" maxLength={4} onChange={this.onChange} className={'form-control ' + (cvvField.isRequiredError ? 'is-invalid' : '')} autoComplete="off" />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { paymentMethod } = this.props;

        if (!this.state.sdkLoaded) return null;

        const paymentMethodText = paymentMethod ? paymentMethod.toLowerCase() : paymentMethod;

        // SlimCD supports credit card payments only
        if (paymentMethodText !== 'creditcard') {
            return <div>Unsupported Payment Method</div>;
        }

        return (
            <div className="slimcd-wrapper">
                {this.renderCreditCard()}

                <div className="text-right mt-3">
                    <button className="btn btn-primary" onClick={this.onPayClick} title="SlimCD Payment" >
                        {T.translate('accountPay.submitPayment')}
                    </button>
                </div>
            </div>
        );
    }
}

export default SlimCDForm;
